import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationUrl } from "../../../hooks/useNavigationUrl";
import { ROUTES } from "../../../constant";
import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { bySource, iSource } from "../../../context/sourceContext/SourceContext";
import { ScreenTypes } from "../../../enums";
import { iChatData } from "../../../types/common";
import { ChatBooking } from "../../../utils/chat/chatCheckout";
import useConfirmBookingAPi from "../../Payments/paymentSuccessPage/hooks/useConfirmBookingAPi";
import useCreateItineraryHook from "../../Payments/paymentSuccessPage/hooks/useCreateItineraryHook";
import { clearChatReturnAnchor } from "../../../services/searchProgress/chatReturnAnchor";
import { clearChatResultsGeneratedAt } from "../../../utils/booking/chatResultsGeneratedAtStore";
import { clearPickSource, getPickSource } from "../../../utils/booking/pickSourceStore";
import { clearShopTravelResultsExpiry } from "../../../utils/booking/shopTravelResultsExpiryStore";
import { MOBILE_MEDIA_QUERY } from "../../../constant/breakpoints";

export type BookedEntry = Omit<ChatBooking, "kind">;

export default function useBookingConfirm(
  session_Id: string,
  source: iSource = "booking",
  withBookedEntry?: (booking: BookedEntry) => iChatData[],
  noPayment = false
) {
  const navigate = useNavigate();
  const { navigateWithParams } = useNavigationUrl();
  const [bookingInProgressFlag, setBookingInprogressFlag] = useState(false);
  const { bookedtransIds, selectedChatData, setFirstScreen, setScreen }: iGlobalContext = useContext(StateContext);
  const selectedChatDataIdRef = useRef<string>("");
  const { itineraryID = "" } = bookedtransIds || {};
  const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  const [isLoading, setIsLoading] = useState(true);
  const [isBookingFailed, setIsBookingFailed] = useState(false);

  useEffect(() => {
    if (!isLoading && isBookingFailed) {
      navigate(ROUTES.PaymentFailure);
    }
  }, [isLoading, isBookingFailed, navigate]);

  useEffect(() => {
    try {
      sessionStorage.removeItem("_zenvoya_booking_retry_count");
      sessionStorage.removeItem("_zenvoya_stripe_error_message");
    } catch {}
  }, []);

  useEffect(() => {
    if (!bySource(source, { booking: true, chat: false, mcp: false })) return;
    function onPopState() {
      try {
        sessionStorage.setItem("_zenvoya_booking_return_target", "search");
      } catch {}
      navigate(ROUTES.BookingInProgress);
    }
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [navigate, source]);

  const toBookedChatData = withBookedEntry
    ? (inProgress: boolean) =>
        withBookedEntry({
          status: inProgress ? "inprogress" : "booked",
          itineraryId: itineraryID,
          tripId: selectedChatData?.id || selectedChatDataIdRef.current || itineraryID,
        })
    : undefined;

  const { confirmBookingAPI } = useConfirmBookingAPi(
    setIsBookingFailed,
    setIsLoading,
    selectedChatDataIdRef,
    session_Id,
    setBookingInprogressFlag,
    noPayment,
    toBookedChatData
  );

  const readAndClearChatOrigin = (): boolean => {
    clearShopTravelResultsExpiry();
    const flight = getPickSource("flight") === "chat";
    const hotel = getPickSource("hotel") === "chat";
    if (flight || hotel) {
      clearPickSource("flight", "hotel");
      clearChatReturnAnchor();
      clearChatResultsGeneratedAt();
      return true;
    }
    return false;
  };

  const handleGotoTrip = () => {
    if (readAndClearChatOrigin()) {
      if (isMobile) {
        navigateWithParams(ROUTES.TopDestination);
      } else {
        navigateWithParams(ROUTES.Home);
        setFirstScreen(ScreenTypes.topDestination);
        setScreen("");
      }
      return;
    }
    navigateWithParams(ROUTES.Home);
    setFirstScreen(ScreenTypes.NewTrip);
    setScreen("");
  };

  const { handleViewItinerary } = useCreateItineraryHook(
    setIsLoading,
    session_Id,
    selectedChatDataIdRef,
    confirmBookingAPI,
    isBookingFailed,
    isMobile,
    noPayment
  );

  return {
    isLoading,
    setIsLoading,
    isBookingFailed,
    setIsBookingFailed,
    bookingInProgressFlag,
    itineraryID,
    isMobile,
    readAndClearChatOrigin,
    handleGotoTrip,
    handleViewItinerary,
  };
}
